import { useMemo, useState } from 'react';
import type { Activity } from '../../types/schedule';
import type { CriticalPathResult } from '../../services/analytics';
import { ChartEmpty, formatDate, useMeasure, useTooltip } from './chartPrimitives';

interface CriticalPathNetworkProps {
  criticalPath: CriticalPathResult;
  onSelectActivity?: (activityId: string) => void;
  selectedActivityId?: string | null;
}

interface LayoutNode {
  activity: Activity;
  layer: number;
  row: number;
  x: number;
  y: number;
}

const NODE_WIDTH = 132;
const NODE_HEIGHT = 42;
const LAYER_GAP = 58;
const ROW_GAP = 14;

/**
 * Layered drawing of the driving path.
 *
 * Nodes are placed by longest-path depth so every relationship points
 * left-to-right, which is what makes a logic diagram readable. Lag is
 * annotated on the link because a lagged link is the thing most likely to be
 * hiding schedule risk.
 */
function layoutNetwork(
  chain: Activity[],
  links: CriticalPathResult['links']
): { nodes: LayoutNode[]; width: number; height: number } {
  if (chain.length === 0) return { nodes: [], width: 0, height: 0 };

  const byId = new Map(chain.map((a) => [a.id, a]));
  const predecessorMap = new Map<string, string[]>();
  for (const link of links) {
    if (!byId.has(link.from) || !byId.has(link.to)) continue;
    const list = predecessorMap.get(link.to);
    if (list) list.push(link.from);
    else predecessorMap.set(link.to, [link.from]);
  }

  // Longest-path layering, memoised with cycle protection — a cyclic network
  // is invalid but should still render rather than hang the tab.
  const layerOf = new Map<string, number>();
  const visiting = new Set<string>();

  const resolveLayer = (id: string): number => {
    const cached = layerOf.get(id);
    if (cached !== undefined) return cached;
    if (visiting.has(id)) return 0;

    visiting.add(id);
    const predecessors = predecessorMap.get(id) ?? [];
    const layer = predecessors.length
      ? Math.max(...predecessors.map((p) => resolveLayer(p) + 1))
      : 0;
    visiting.delete(id);
    layerOf.set(id, layer);
    return layer;
  };

  for (const activity of chain) resolveLayer(activity.id);

  const byLayer = new Map<number, Activity[]>();
  for (const activity of chain) {
    const layer = layerOf.get(activity.id) ?? 0;
    const list = byLayer.get(layer);
    if (list) list.push(activity);
    else byLayer.set(layer, [activity]);
  }

  const nodes: LayoutNode[] = [];
  let maxRows = 0;

  for (const [layer, activities] of [...byLayer.entries()].sort((a, b) => a[0] - b[0])) {
    activities.sort(
      (a, b) => (a.start?.getTime() ?? 0) - (b.start?.getTime() ?? 0)
    );
    maxRows = Math.max(maxRows, activities.length);
    activities.forEach((activity, row) => {
      nodes.push({
        activity,
        layer,
        row,
        x: layer * (NODE_WIDTH + LAYER_GAP),
        y: row * (NODE_HEIGHT + ROW_GAP),
      });
    });
  }

  const layerCount = byLayer.size;
  return {
    nodes,
    width: layerCount * NODE_WIDTH + Math.max(0, layerCount - 1) * LAYER_GAP,
    height: maxRows * NODE_HEIGHT + Math.max(0, maxRows - 1) * ROW_GAP,
  };
}

export function CriticalPathNetwork({
  criticalPath,
  onSelectActivity,
  selectedActivityId,
}: CriticalPathNetworkProps) {
  const [containerRef, { width: containerWidth }] = useMeasure<HTMLDivElement>();
  const tooltip = useTooltip();
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // A 400-node diagram is unreadable and slow; cap and say so rather than
  // silently drawing a hairball.
  const CAP = 120;
  const capped = criticalPath.chain.length > CAP;
  const chain = useMemo(
    () => criticalPath.chain.slice(0, CAP),
    [criticalPath.chain]
  );

  const chainIds = useMemo(() => new Set(chain.map((a) => a.id)), [chain]);
  const links = useMemo(
    () => criticalPath.links.filter((l) => chainIds.has(l.from) && chainIds.has(l.to)),
    [criticalPath.links, chainIds]
  );

  const { nodes, width, height } = useMemo(
    () => layoutNetwork(chain, links),
    [chain, links]
  );

  const nodeById = useMemo(
    () => new Map(nodes.map((n) => [n.activity.id, n])),
    [nodes]
  );

  if (chain.length === 0) {
    return (
      <ChartEmpty message="No critical or driving-path activities found in this schedule." />
    );
  }

  const padding = 18;
  const svgWidth = width + padding * 2;
  const svgHeight = height + padding * 2;

  const connectedToHover = new Set<string>();
  if (hoveredId) {
    connectedToHover.add(hoveredId);
    for (const link of links) {
      if (link.from === hoveredId) connectedToHover.add(link.to);
      if (link.to === hoveredId) connectedToHover.add(link.from);
    }
  }

  return (
    <div className="w-full">
      <div
        ref={containerRef}
        className="w-full overflow-auto"
        style={{ maxHeight: 460, background: 'var(--surface-inset)', borderRadius: 3 }}
      >
        {containerWidth > 0 && (
          <svg
            width={Math.max(svgWidth, containerWidth)}
            height={svgHeight}
            role="img"
            aria-label="Critical path logic network"
          >
            <defs>
              <marker
                id="cpm-arrow"
                viewBox="0 0 8 8"
                refX="7"
                refY="4"
                markerWidth="6"
                markerHeight="6"
                orient="auto-start-reverse"
              >
                <path d="M 0 1 L 7 4 L 0 7 z" fill="var(--border-strong)" />
              </marker>
            </defs>

            <g transform={`translate(${padding},${padding})`}>
              {links.map((link, index) => {
                const from = nodeById.get(link.from);
                const to = nodeById.get(link.to);
                if (!from || !to) return null;

                const x1 = from.x + NODE_WIDTH;
                const y1 = from.y + NODE_HEIGHT / 2;
                const x2 = to.x;
                const y2 = to.y + NODE_HEIGHT / 2;
                const midX = (x1 + x2) / 2;

                const dimmed =
                  hoveredId !== null &&
                  !(connectedToHover.has(link.from) && connectedToHover.has(link.to));
                const hasLag = Math.abs(link.lagDays) >= 0.5;

                return (
                  <g key={`${link.from}-${link.to}-${index}`} opacity={dimmed ? 0.15 : 1}>
                    <path
                      d={`M${x1},${y1} C${midX},${y1} ${midX},${y2} ${x2},${y2}`}
                      fill="none"
                      stroke={hasLag ? 'var(--status-warn)' : 'var(--border-strong)'}
                      strokeWidth={hasLag ? 1.6 : 1.2}
                      markerEnd="url(#cpm-arrow)"
                    />
                    {(hasLag || link.type !== 'FS') && (
                      <text
                        x={midX}
                        y={(y1 + y2) / 2 - 4}
                        textAnchor="middle"
                        className="chart-axis-label tnum"
                        style={{
                          fill: hasLag ? 'var(--status-warn)' : 'var(--text-muted)',
                        }}
                      >
                        {link.type}
                        {hasLag ? ` ${link.lagDays > 0 ? '+' : ''}${link.lagDays.toFixed(0)}d` : ''}
                      </text>
                    )}
                  </g>
                );
              })}

              {nodes.map(({ activity, x, y }) => {
                const isSelected = activity.id === selectedActivityId;
                const dimmed = hoveredId !== null && !connectedToHover.has(activity.id);
                const isNegative = activity.totalFloatDays < 0;

                return (
                  <g
                    key={activity.id}
                    className="chart-hit"
                    opacity={dimmed ? 0.22 : 1}
                    onMouseEnter={() => setHoveredId(activity.id)}
                    onMouseMove={(event) =>
                      tooltip.show(
                        <div>
                          <div className="font-semibold">{activity.code}</div>
                          <div style={{ color: 'var(--text-secondary)' }}>
                            {activity.name}
                          </div>
                          <div className="tnum" style={{ marginTop: 4 }}>
                            {formatDate(activity.start)} → {formatDate(activity.finish)}
                          </div>
                          <div className="tnum">
                            {activity.originalDurationDays.toFixed(0)}d duration ·{' '}
                            {activity.totalFloatDays.toFixed(0)}d float
                          </div>
                          <div style={{ color: 'var(--text-muted)' }}>
                            {activity.status}
                          </div>
                        </div>,
                        event
                      )
                    }
                    onMouseLeave={() => {
                      setHoveredId(null);
                      tooltip.hide();
                    }}
                    onClick={() => onSelectActivity?.(activity.id)}
                  >
                    <rect
                      x={x}
                      y={y}
                      width={NODE_WIDTH}
                      height={NODE_HEIGHT}
                      rx={2}
                      fill="var(--surface-card)"
                      stroke={
                        isSelected
                          ? 'var(--mr-vivid-blue)'
                          : isNegative
                            ? 'var(--status-fail)'
                            : 'var(--border-strong)'
                      }
                      strokeWidth={isSelected ? 2 : 1}
                    />
                    <rect
                      x={x}
                      y={y}
                      width={3}
                      height={NODE_HEIGHT}
                      fill={isNegative ? 'var(--status-fail)' : 'var(--mr-vivid-blue)'}
                    />
                    <text
                      x={x + 10}
                      y={y + 16}
                      className="tnum"
                      style={{ fill: 'var(--text-primary)', fontSize: 10, fontWeight: 600 }}
                    >
                      {activity.code.length > 16
                        ? `${activity.code.slice(0, 15)}…`
                        : activity.code}
                    </text>
                    <text
                      x={x + 10}
                      y={y + 30}
                      style={{ fill: 'var(--text-muted)', fontSize: 9.5 }}
                    >
                      {activity.name.length > 19
                        ? `${activity.name.slice(0, 18)}…`
                        : activity.name}
                    </text>
                  </g>
                );
              })}
            </g>
          </svg>
        )}
      </div>

      <p className="mt-2 text-xs" style={{ color: 'var(--text-muted)' }}>
        {criticalPath.usedDrivingFlag
          ? "Using P6's longest-path flag."
          : 'Using total float ≤ 0 — this file was not scheduled with multiple float paths.'}{' '}
        {chain.length} activities, {criticalPath.lengthDays.toFixed(0)} days of duration.
        {capped && ` Showing the first ${CAP} of ${criticalPath.chain.length}.`}{' '}
        Amber links carry lag.
      </p>
    </div>
  );
}
